import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { CreateSyslog } from './components/ModalSyslog'
import { useAppSelector } from '../../../../redux/hooks'
import Swal from 'sweetalert2'
import { useAuth } from '../../auth'
import Spinner from '../../custom/Spinner/Spinner'
import { getListServerLogs, deleteOneServerLog } from './request/request'
import styles from './SysLogCrudTable.module.scss'

// Icons for sorting
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ImportExportIcon from '@mui/icons-material/ImportExport'

const API_URL = process.env.REACT_APP_API_URL

type ID = undefined | null | number

function SyslogCrud(props: any) {
    // Sub/Tenant context
    const TenantId = useAppSelector((state) => state.TenantSlice.id)
    const SubscriptionId = useAppSelector((state) => state.SuscriptionSlice.id)
    const Cloud = useAppSelector((state) => state.CloudSlice.Type)
    const Region = useAppSelector((state) => state.RegionSlice.name)

    const { currentUser } = useAuth()

    // Table data & loading
    const [Records, setRecords] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    // Sorting
    const [sortColumn, setSortColumn] = useState<string>('')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

    // Search
    const [searchTerm, setSearchTerm] = useState('')
    const [searchColumn, setSearchColumn] = useState('All')

    // Pagination (client-side)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(5)

    // Modal control
    const [ID, setID] = useState<ID>(undefined)

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortColumn(column)
            setSortDirection('asc')
        }
    }

    const getSortIcon = (col: string) => {
        if (sortColumn === col) {
            return sortDirection === 'asc' ? (
                <KeyboardArrowUpIcon style={{ verticalAlign: 'middle' }} />
            ) : (
                <KeyboardArrowDownIcon style={{ verticalAlign: 'middle' }} />
            )
        } else {
            return (
                <ImportExportIcon style={{ verticalAlign: 'middle', opacity: 0.7 }} />
            )
        }
    }

    // Filtering logic
    function applySearch(data: any[]) {
        if (!searchTerm) return data
        const lowerTerm = searchTerm.toLowerCase()

        return data.filter((d: any) => {
            if (searchColumn === 'All') {
                return (
                    d.ip?.toLowerCase().includes(lowerTerm) ||
                    d.port?.toString().toLowerCase().includes(lowerTerm) ||
                    d.description?.toLowerCase().includes(lowerTerm) ||
                    d.ctxr_amount?.toString().toLowerCase().includes(lowerTerm) ||
                    d.subscription?.toString().toLowerCase().includes(lowerTerm)
                )
            } else {
                let val = ''
                switch (searchColumn) {
                    case 'ip':
                        val = d.ip?.toLowerCase() || ''
                        break
                    case 'port':
                        val = d.port?.toString().toLowerCase() || ''
                        break
                    case 'description':
                        val = d.description?.toLowerCase() || ''
                        break
                    case 'ctxr_amount':
                        val = d.ctxr_amount?.toString().toLowerCase() || ''
                        break
                    case 'subscription':
                        val = d.subscription?.toString().toLowerCase() || ''
                        break
                    default:
                        val = ''
                }
                return val.includes(lowerTerm)
            }
        })
    }

    // Sorting logic
    function applySort(data: any[]) {
        if (!sortColumn) return data
        const sorted = [...data].sort((a, b) => {
            const valA = a[sortColumn]?.toString().toLowerCase() || ''
            const valB = b[sortColumn]?.toString().toLowerCase() || ''
            if (valA < valB) return sortDirection === 'asc' ? -1 : 1
            if (valA > valB) return sortDirection === 'asc' ? 1 : -1
            return 0
        })
        return sorted
    }

    // Gather final data
    const filteredData = applySort(applySearch(Records))
    const totalItems = filteredData.length
    const totalPages = Math.ceil(totalItems / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const paginatedData = filteredData.slice(startIndex, endIndex)

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page)
        }
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
        setCurrentPage(1)
    }

    // Data load from server (like a full list)
    const fetchData = async () => {
        try {
            if (SubscriptionId !== 0) {
                setLoading(true)
                const response = await getListServerLogs(SubscriptionId, 1, 99999, TenantId)
                setRecords(response.data.data || [])
            }
        } catch (error) {
            console.error('Error loading logs:', error)
        } finally {
            setLoading(false)
        }
    }

    // Delete logic
    const [swalInput, setSwalInput] = useState('')
    const deleteServerLog = async (id: any) => {
        try {
            Swal.fire({
                position: 'center',
                icon: 'warning',
                title: 'Are you sure?',
                text: 'Once deleted, you will not be able to recover this ServerLog!',
                inputLabel: 'Enter "confirm" to continue...',
                input: 'text',
                inputPlaceholder: 'confirm',
                inputValue: swalInput,
                showCancelButton: true,
                confirmButtonColor: '#00BCD1',
                cancelButtonColor: '#FF4089',
                confirmButtonText: 'Yes, delete it!',
                preConfirm: (value) => {
                    if (!value.toLowerCase().includes('confirm')) {
                        Swal.showValidationMessage(
                            'Please write the word "confirm" to continue.'
                        )
                    } else {
                        setSwalInput(value)
                    }
                },
            }).then(async (result) => {
                if (result.isConfirmed) {
                    setSwalInput('')
                    try {
                        await deleteOneServerLog(SubscriptionId, id, TenantId)
                        Swal.fire({
                            position: 'center',
                            icon: 'success',
                            title: 'Your syslog server has been deleted.',
                            showConfirmButton: false,
                            timer: 3000,
                        })
                        fetchData()
                    } catch (error: any) {
                        Swal.fire({
                            position: 'center',
                            icon: 'error',
                            title:
                                'The syslog server could not be deleted.\n error code: ' +
                                error.response?.status +
                                '\nerror message: ' +
                                error.response?.data.error,
                            showConfirmButton: false,
                            timer: 3500,
                        })
                    }
                } else {
                    Swal.fire({ title: 'Your syslog server is safe!', icon: 'success' })
                }
            })
        } catch (error) {
            console.error('Error while deleting syslog server:', error)
        }
    }

    // Mount effect
    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Region])

    return (
        <>
            {loading && <Spinner />}

            <div className='card' style={{ minHeight: '75vh' }}>
                <div className='card-header border-0 pt-5'>
                    <h3 className='card-title align-items-start flex-column '>
                        <span className='f-titulo text-marino fs-md'></span>
                    </h3>
                    {currentUser?.ListPermiso?.includes('CTXR.Neighbor.Add') && (
                        <button onClick={() => setID(null)} className='btn-light-marino f-parrafo'>
                            Add Syslog Server
                        </button>
                    )}
                </div>

                <div className='card-body py-3'>
                    <div className='tab-content'>
                        <div className='tab-pane fade show active' id='kt_table_widget_5_tab_1'>
                            <div className='table-responsive'>
                                {Records.length > 0 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '10px',
                                            marginRight: '5px',
                                        }}
                                    >
                                        <div>
                                            <span className='text-marino f-parrafo fw-semibold'>
                                                Total Records: {totalItems}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', height: '30px' }}>
                                            <div
                                                className='input-group custom-search-group'
                                                style={{ width: '400px', display: 'flex', height: '30px' }}
                                            >
                                                <span
                                                    className='input-group-text'
                                                    style={{
                                                        justifyContent: 'center',
                                                        height: '30px',
                                                        lineHeight: '30px',
                                                        padding: '0 8px',
                                                    }}
                                                >
                                                    <i
                                                        className='bi-search'
                                                        style={{ fontSize: '1rem', color: '#FF4089', verticalAlign: 'middle' }}
                                                    ></i>
                                                </span>
                                                <select
                                                    className='form-select'
                                                    style={{
                                                        border: '1px solid var(--bs-gray-300)',
                                                        width: '130px',
                                                        height: '30px',
                                                        lineHeight: '30px',
                                                        padding: '0 8px',
                                                        fontSize: '0.875rem',
                                                    }}
                                                    value={searchColumn}
                                                    onChange={(e) => {
                                                        setSearchColumn(e.target.value)
                                                        setCurrentPage(1)
                                                    }}
                                                >
                                                    <option value='All'>All</option>
                                                    <option value='ip'>Server IP</option>
                                                    <option value='port'>Port</option>
                                                    <option value='description'>Description</option>
                                                    <option value='ctxr_amount'>CTX-R Count</option>
                                                    {/* We add subscription to the search columns as well */}
                                                    {SubscriptionId === -1 && (
                                                        <option value='subscription'>Subscription</option>
                                                    )}
                                                </select>
                                                <input
                                                    type='text'
                                                    className='form-control'
                                                    style={{
                                                        width: '200px',
                                                        height: '30px',
                                                        lineHeight: '30px',
                                                        padding: '0 8px',
                                                        fontSize: '0.875rem',
                                                    }}
                                                    placeholder={`Search by ${searchColumn === 'All' ? 'All' : searchColumn
                                                        }`}
                                                    value={searchTerm}
                                                    onChange={handleSearchChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <table className='w-full f-parrafo caption-bottom custom-table' id='table'>
                                    <thead>
                                        <tr className='text-azul'>
                                            <th className='table-cell' onClick={() => handleSort('ip')}>
                                                <div className={styles['header-content']}>
                                                    <span>Server IP</span>
                                                    <span className={styles['sortable-column']}>{getSortIcon('ip')}</span>
                                                </div>
                                            </th>
                                            <th className='table-cell' onClick={() => handleSort('port')}>
                                                <div className={styles['header-content']}>
                                                    <span>Port</span>
                                                    <span className={styles['sortable-column']}>{getSortIcon('port')}</span>
                                                </div>
                                            </th>
                                            <th className='table-cell' onClick={() => handleSort('description')}>
                                                <div className={styles['header-content']}>
                                                    <span>Description</span>
                                                    <span className={styles['sortable-column']}>{getSortIcon('description')}</span>
                                                </div>
                                            </th>
                                            <th className='table-cell' onClick={() => handleSort('ctxr_amount')}>
                                                <div className={styles['header-content']}>
                                                    <span>Associated CTX-R's</span>
                                                    <span className={styles['sortable-column']}>
                                                        {getSortIcon('ctxr_amount')}
                                                    </span>
                                                </div>
                                            </th>
                                            {SubscriptionId === -1 && (
                                                <th
                                                    className='table-cell'
                                                    onClick={() => handleSort('subscription')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className={styles['header-content']}>
                                                        <span>Subscription</span>
                                                        <span className={styles['sortable-column']}>
                                                            {getSortIcon('subscription')}
                                                        </span>
                                                    </div>
                                                </th>
                                            )}
                                            <th className='table-cell' style={{ width: '200px' }}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedData.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={SubscriptionId === -1 ? 6 : 5}
                                                    style={{
                                                        padding: '0',
                                                        border: 'none'
                                                    }}
                                                >
                                                    {/* FLEX container to center content */}
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            minHeight: '100px',
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <span style={{ color: '#FF4089', fontWeight: 'bold' }}>
                                                            No Data Available
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedData.map((d: any, i: any) => (
                                                <tr key={i} className='table-row'>
                                                    <td className='table-cell'>{d.ip}</td>
                                                    <td className='table-cell'>{d.port}</td>
                                                    <td className='table-cell'>{d.description}</td>
                                                    <td className='table-cell'>
                                                        {d.ctxr_amount !== null && d.ctxr_amount !== undefined
                                                            ? d.ctxr_amount
                                                            : 'No data available'}
                                                    </td>
                                                    {SubscriptionId === -1 && (
                                                        <td className='table-cell'>{d.subscription}</td>
                                                    )}
                                                    <td
                                                        className='table-cell'
                                                        style={{ display: 'flex', justifyContent: 'center', width: '200px' }}
                                                    >
                                                        <div style={{ display: 'flex', gap: '10px' }}>
                                                            {currentUser?.ListPermiso?.includes('CTXR.Neighbor.Update') && (
                                                                <button
                                                                    className='btn btn-marino f-parrafo'
                                                                    onClick={() => setID(d.id)}
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                            {currentUser?.ListPermiso?.includes('CTXR.Neighbor.Delete') && (
                                                                <button
                                                                    className='btn btn-danger f-parrafo'
                                                                    onClick={() => deleteServerLog(d.id)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>

                                {filteredData.length > 0 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginTop: '10px',
                                        }}
                                    >
                                        <div>
                                            {currentPage > 1 && (
                                                <button
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    className={`btn btn-secondary ${styles.buttonRows}`}
                                                >
                                                    Previous
                                                </button>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span>
                                                Page {currentPage} of {totalPages}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            {currentPage < totalPages && (
                                                <button
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    className={`btn btn-secondary ${styles.buttonRows}`}
                                                >
                                                    Next
                                                </button>
                                            )}
                                            <div className={`select-container ${styles.selectContainer}`}>
                                                <select
                                                    className={`custom-select aligned-select text-center ${styles.selectStyle}`}
                                                    aria-label='Select items per page'
                                                    value={itemsPerPage}
                                                    onChange={(e) => {
                                                        setItemsPerPage(Number(e.target.value))
                                                        setCurrentPage(1)
                                                    }}
                                                >
                                                    <option value={5}>5 rows</option>
                                                    <option value={10}>10 rows</option>
                                                    <option value={20}>20 rows</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {ID !== undefined && (
                <CreateSyslog setID={setID} id={ID} setRecords={setRecords} reload={fetchData} />
            )}
            <br />
        </>
    )
}

export default SyslogCrud
